import { useEffect, useState } from "react";
import Loader from "./Loader";
import { diff as versionDiff } from "semver";
import './PluginTable.scss';

function formatVersion(version: string): string {
    if (/^[0-9]+\.[0-9]+$/.test(version)) {
        return version + ".0";
    }
    return version;
}

function normalizeVersion(version: string): string {
    if (typeof version !== "string") {
        return "0.0.0";
    }
    let clean = version.trim();
    const parts = clean.split(".");
    if (parts.length > 3) {
        clean = parts.slice(0, 3).join(".");
    }
    if (parts.length === 2) {
        clean = clean + ".0";
    }
    return clean;
}

const PluginTable = ({ pluginBySite }: { pluginBySite: Promise<Record<string, string[][]>>; }) => {
    const [pluginData, setPluginData] = useState<Record<string, string[][]> | null>(null);

    // gives the data to pluginData each time pluginBySite is rendered
    useEffect(() => {
        pluginBySite.then((value) => {
            setPluginData(value);
        });
    }, [pluginBySite]);
    return (
        <article>
            {/* if pluginData exists render it otherwise render Loading */}
            {pluginData ? (
                Object.entries(pluginData).map(([plugin, sites]) => {
                    const latestVersion = sites[0][0]; // assuming version is index 1
                    const outdatedCount = sites.slice(1).filter(site => site[1] !== latestVersion);
                    const siteCounter = sites.length - 1 /* -1 because the first one is the latest version of the plugin*/
                    return (
                        <details key={plugin}>
                            <summary>
                                <div className="plugin-list__infos">
                                    <header>
                                        <h3 className="site-list__title">{plugin}</h3>
                                        <div className="priority-tag">
                                            <p>{sites[0]}</p>{/* get the latest version of the plugin */}
                                        </div>
                                        <div className="priority-tag">
                                            <p>{siteCounter.toString() + " total"}</p>
                                        </div>
                                        {outdatedCount.length > 0 ? (
                                            <div className="priority-tag plugin-tag-to-update">
                                                <p>{outdatedCount.length.toString() + " to update"}</p>{/* -1 because the first one is the latest version of the plugin*/}
                                            </div>
                                        ) : null}
                                    </header>
                                </div>
                            </summary>
                            <div className="plugin-site table__container display-none">
                                <table>
                                    <tbody>
                                        {/* Pass through all the arrays of sites except the first one because its the latest version of the plugin */}
                                        {sites.slice(1).map((infos, index, array) => {
                                            const siteVersion = normalizeVersion(infos[1]);
                                            const latestVersion = normalizeVersion(sites[0][0]); // adjust if sites[0] stores differently
                                            let diff: string | null = null;
                                            try {
                                                diff = versionDiff(siteVersion, latestVersion);
                                            } catch (e) {
                                                console.warn("Could not compare versions:", siteVersion, latestVersion, e);
                                            }

                                            return (
                                                <tr key={index} className={'site-table__row site-table__row--' + (diff || 'equal')}>
                                                    {index === 0 && (
                                                        <th scope="rowgroup" rowSpan={array.length}>
                                                            Sites
                                                        </th>
                                                    )}
                                                    {infos.map((info, idx) =>
                                                        idx === 2 ? (
                                                            <td className={"site-option-" + idx} key={`${index}-${idx}`}>
                                                                <a href={info}>{info}</a>
                                                            </td>
                                                        ) : (
                                                            <td
                                                                className={
                                                                    "site-option-" +
                                                                    idx +
                                                                    (idx === 1 && diff ? " site-table__row--major" : "")
                                                                }
                                                                key={`${index}-${idx}`}
                                                            >
                                                                {info}
                                                            </td>
                                                        )
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </details>
                    )
                })
            ) : (
                <Loader />
            )}
        </article>
    );
}

export default PluginTable;
